import { useRef } from 'react';
import { InteractionEvent, Point } from 'pixi.js';

type ReturnData = [(event: InteractionEvent) => void, (event: InteractionEvent, onIntendedClick: () => void) => void];

// The max distance between a pointer up and down event to be considered a click
const CLICK_DELTA_LIMIT = 5;

/**
 * The hook creates pointer down and pointer up wrappers which are to be used on UI elements that are
 * meant to perform an action only when the position of the cursor is the same when pointer down and
 * pointer up have been triggered.
 *
 * When a user clicks on the element and drags the cursor away before releasing it, the action will
 * not be performed.
 * @returns {ReturnData}
 */
export function useNonDragClick(): ReturnData {
  /**
   * We record the position at which the pointer down action has been performed at so we can compare it to
   * the pointer up coordinates.
   */
  const pointerDownCoordinates = useRef(
    new Point(undefined, undefined),
  );

  const onPointerDown = (event: InteractionEvent) => {
    event.stopPropagation();

    const { x, y } = event.data.global;

    pointerDownCoordinates.current.set(x, y);
  };

  const onPointerUp = (event: InteractionEvent, onNonDragClick: () => void) => {
    event.stopPropagation();
    const clickedPoint = event.data.global;

    /**
     * When the pointer down and up coordinates are roughly the same,
     * it means that the user indeded to click on the element.
     * This is helpful when the user is panning across a map with draggable objects.
     */
    const xDelta = Math.abs(pointerDownCoordinates.current.x - clickedPoint.x);
    const yDelta = Math.abs(pointerDownCoordinates.current.y - clickedPoint.y);

    if (xDelta < CLICK_DELTA_LIMIT && yDelta < CLICK_DELTA_LIMIT) {
      onNonDragClick();
    }

    pointerDownCoordinates.current.set(undefined, undefined);
  };

  return [
    onPointerDown,
    onPointerUp,
  ];
}

export default {};
